import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, TextField, InputAdornment, IconButton } from "@mui/material";
import BookOrderDialogShell from "../MultiAccountOrderModal/BookOrderDialogShell";
import ModalBidAskField, { DEFAULT_MODAL_BID_ASK_ORDER_TYPE } from "../MultiAccountOrderModal/ModalBidAskField";
import InstrumentTradingSymbolField from "../InstrumentTradingSymbolField";
import { QUANTITY_SPINNER_DOWN, QUANTITY_SPINNER_UP } from "../QuantitySpinnerIcons/QuantitySpinnerIcons";
import modalStyles from "../MultiAccountOrderModal/MultiAccountOrderModal.module.css";
import {
  buildRepriceConfirmPayload,
  buildRepriceGroupedData,
  collectRepriceModalIds,
  DEFAULT_BID_ASK_PCT,
} from "./repriceModalUtils";

function RepriceModal({
  open,
  onClose,
  onConfirm,
  selectedRows = [],
  submitting = false,
  defaultBidAskPct1 = DEFAULT_BID_ASK_PCT,
  defaultBidAskPct2 = DEFAULT_BID_ASK_PCT,
  groupOptions,
}) {
  const [rowData, setRowData] = useState({});
  const [symbolOverrides, setSymbolOverrides] = useState({});
  const [initiateInstrumentsBySymbol, setInitiateInstrumentsBySymbol] = useState({});
  const [globalBidAskPct1, setGlobalBidAskPct1] = useState(defaultBidAskPct1);
  const [globalBidAskPct2, setGlobalBidAskPct2] = useState(defaultBidAskPct2);
  const [globalOrderType1, setGlobalOrderType1] = useState(DEFAULT_MODAL_BID_ASK_ORDER_TYPE);
  const [globalOrderType2, setGlobalOrderType2] = useState(DEFAULT_MODAL_BID_ASK_ORDER_TYPE);

  const groupedData = useMemo(
    () =>
      buildRepriceGroupedData(selectedRows, {
        defaultBidAskPct1,
        defaultBidAskPct2,
        ...groupOptions,
      }),
    [selectedRows, defaultBidAskPct1, defaultBidAskPct2, groupOptions]
  );

  const groupedDataKeys = useMemo(() => Object.keys(groupedData), [groupedData]);
  const hasGroupedData = groupedDataKeys.length > 0;

  const resetState = useCallback(() => {
    setRowData({});
    setSymbolOverrides({});
    setInitiateInstrumentsBySymbol({});
    setGlobalBidAskPct1(defaultBidAskPct1);
    setGlobalBidAskPct2(defaultBidAskPct2);
    setGlobalOrderType1(DEFAULT_MODAL_BID_ASK_ORDER_TYPE);
    setGlobalOrderType2(DEFAULT_MODAL_BID_ASK_ORDER_TYPE);
  }, [defaultBidAskPct1, defaultBidAskPct2]);

  useEffect(() => {
    if (!open) {
      resetState();
    }
  }, [open, resetState]);

  const handleClose = useCallback(() => {
    if (submitting) return;
    onClose?.();
  }, [onClose, submitting]);

  const handleRowDataChange = useCallback((modalId, field, value) => {
    setRowData((prev) => {
      const currentValue = prev[modalId]?.[field];
      if (currentValue === value) return prev;
      return {
        ...prev,
        [modalId]: {
          ...prev[modalId],
          [field]: value,
        },
      };
    });
  }, []);

  const handleTradingSymbolChange = useCallback((originalSymbol, newSymbol, instrumentMeta) => {
    setSymbolOverrides((prev) => ({
      ...prev,
      [originalSymbol]: newSymbol,
    }));

    if (instrumentMeta) {
      setInitiateInstrumentsBySymbol((prev) => ({
        ...prev,
        [originalSymbol]: {
          exchange_trading_symbol: newSymbol,
          exchange: instrumentMeta.exchange,
          exchange_token: instrumentMeta.exchange_token,
        },
      }));
    }
  }, []);

  const getRepriceTradingSymbol = useCallback(
    (originalSymbol) => symbolOverrides[originalSymbol] || originalSymbol,
    [symbolOverrides]
  );

  const getRowValue = useCallback(
    (row, field) => {
      const stateValue = rowData[row.modalId]?.[field];

      if (stateValue !== undefined) {
        return stateValue;
      }

      if (field === "bidAskPct1") return globalBidAskPct1;
      if (field === "bidAskPct2") return globalBidAskPct2;
      if (field === "orderType1") return globalOrderType1;
      if (field === "orderType2") return globalOrderType2;

      return row[field] ?? "";
    },
    [rowData, globalBidAskPct1, globalBidAskPct2, globalOrderType1, globalOrderType2]
  );

  const applyGlobalFieldChange = useCallback((field, value, setter) => {
    setter(value);
    setRowData((prev) => {
      const modalIds = collectRepriceModalIds(groupedData);
      const updated = { ...prev };
      let hasChanges = false;

      modalIds.forEach((modalId) => {
        if (updated[modalId]?.[field] !== value) {
          updated[modalId] = {
            ...updated[modalId],
            [field]: value,
          };
          hasChanges = true;
        }
      });

      return hasChanges ? updated : prev;
    });
  }, [groupedData]);

  const handleGlobalBidAskPct1Change = useCallback(
    (value) => applyGlobalFieldChange("bidAskPct1", value, setGlobalBidAskPct1),
    [applyGlobalFieldChange]
  );

  const handleGlobalBidAskPct2Change = useCallback(
    (value) => applyGlobalFieldChange("bidAskPct2", value, setGlobalBidAskPct2),
    [applyGlobalFieldChange]
  );

  const handleGlobalOrderType1Change = useCallback(
    (value) => applyGlobalFieldChange("orderType1", value, setGlobalOrderType1),
    [applyGlobalFieldChange]
  );

  const handleGlobalOrderType2Change = useCallback(
    (value) => applyGlobalFieldChange("orderType2", value, setGlobalOrderType2),
    [applyGlobalFieldChange]
  );

  const createSpinnerHandlers = useCallback(
    (row) => ({
      onQuantityIncrement: () => {
        const currentQty = getRowValue(row, "quantity");
        handleRowDataChange(row.modalId, "quantity", (parseFloat(currentQty) || 0) + 1);
      },
      onQuantityDecrement: () => {
        const currentQty = getRowValue(row, "quantity");
        handleRowDataChange(row.modalId, "quantity", Math.max(0, (parseFloat(currentQty) || 0) - 1));
      },
    }),
    [getRowValue, handleRowDataChange]
  );

  const handleConfirm = useCallback(async () => {
    const payload = buildRepriceConfirmPayload(groupedData, rowData, symbolOverrides);
    await onConfirm?.({
      ...payload,
      initiateInstrumentsBySymbol,
    });
  }, [groupedData, initiateInstrumentsBySymbol, onConfirm, rowData, symbolOverrides]);

  const header = useMemo(
    () => (
      <div className={modalStyles["modal-header-stacked"]}>
        <div className={modalStyles["modal-header"]}>
          <div className={modalStyles["modal-header-cell"]}>
            <span className={modalStyles["modal-header-label"]}>Trading Symbol</span>
          </div>
          <div className={modalStyles["modal-header-cell"]}>
            <span className={modalStyles["modal-header-label"]}>Reprice Trading Symbol</span>
          </div>
          <div className={modalStyles["modal-header-cell"]}>
            <span className={modalStyles["modal-header-label"]}>Accounts</span>
          </div>
          <div className={modalStyles["modal-header-cell"]}>
            <span className={`${modalStyles["modal-header-label"]} ${modalStyles["modal-header-label-qty"]}`}>
              Quantity
            </span>
          </div>
          <div className={modalStyles["modal-header-cell"]}>
            <span className={modalStyles["modal-header-label"]}>Square Off</span>
          </div>
          <div className={modalStyles["modal-header-cell"]}>
            <span className={modalStyles["modal-header-label"]}>Initiate</span>
          </div>
        </div>
        <div className={`${modalStyles["modal-header"]} ${modalStyles["modal-header-inputs-row"]}`}>
          <div
            className={`${modalStyles["modal-header-cell"]} ${modalStyles["modal-header-inputs-spacer"]}`}
            aria-hidden="true"
          />
          <div
            className={`${modalStyles["modal-header-cell"]} ${modalStyles["modal-header-inputs-spacer"]}`}
            aria-hidden="true"
          />
          <div
            className={`${modalStyles["modal-header-cell"]} ${modalStyles["modal-header-inputs-spacer"]}`}
            aria-hidden="true"
          />
          <div
            className={`${modalStyles["modal-header-cell"]} ${modalStyles["modal-header-inputs-spacer"]}`}
            aria-hidden="true"
          />
          <div className={modalStyles["modal-header-cell"]}>
            <ModalBidAskField
              orderType={globalOrderType1}
              onOrderTypeChange={(e) => handleGlobalOrderType1Change(e.target.value)}
              value={globalBidAskPct1}
              onChange={(e) => handleGlobalBidAskPct1Change(parseFloat(e.target.value) || 0)}
              disabled={submitting}
            />
          </div>
          <div className={modalStyles["modal-header-cell"]}>
            <ModalBidAskField
              orderType={globalOrderType2}
              onOrderTypeChange={(e) => handleGlobalOrderType2Change(e.target.value)}
              value={globalBidAskPct2}
              onChange={(e) => handleGlobalBidAskPct2Change(parseFloat(e.target.value) || 0)}
              disabled={submitting}
            />
          </div>
        </div>
      </div>
    ),
    [
      globalBidAskPct1,
      globalBidAskPct2,
      globalOrderType1,
      globalOrderType2,
      handleGlobalBidAskPct1Change,
      handleGlobalBidAskPct2Change,
      handleGlobalOrderType1Change,
      handleGlobalOrderType2Change,
      submitting,
    ]
  );

  const body = useMemo(() => {
    if (!hasGroupedData) {
      return <div className={modalStyles["modal-empty"]}>No data available</div>;
    }

    return groupedDataKeys.map((symbol) => {
      const groupData = groupedData[symbol];
      const rows = groupData.rows || [];
      const repriceSymbol = getRepriceTradingSymbol(symbol);
      const groupExchange = rows[0]?.exchange || "NSE";

      return (
        <div key={symbol} className={modalStyles["modal-group-container"]}>
          <div className={modalStyles["modal-group"]}>
            <div className={modalStyles["modal-symbol-cell"]}>
              <span className={modalStyles["modal-symbol-label"]}>{symbol}</span>
            </div>
            <div className={modalStyles["modal-reprice-symbol-cell"]}>
              <InstrumentTradingSymbolField
                key={symbol}
                nearSymbol={symbol}
                value={repriceSymbol}
                exchange={groupExchange}
                disabled={submitting}
                onChange={(newSymbol, instrumentMeta) =>
                  handleTradingSymbolChange(symbol, newSymbol, instrumentMeta)
                }
              />
            </div>
            <div className={modalStyles["modal-rows-container"]}>
              {rows.map((row) => {
                const quantity = getRowValue(row, "quantity");
                const bidAskPct1 = getRowValue(row, "bidAskPct1");
                const bidAskPct2 = getRowValue(row, "bidAskPct2");
                const orderType1 = getRowValue(row, "orderType1");
                const orderType2 = getRowValue(row, "orderType2");
                const spinnerHandlers = createSpinnerHandlers(row);

                return (
                  <div key={row.modalId} className={modalStyles["modal-row"]}>
                    <div className={modalStyles["modal-account-cell"]}>
                      <span className={modalStyles["modal-account-label"]}>
                        {row.broker_client_code || "-"}
                      </span>
                    </div>
                    <div className={modalStyles["modal-quantity-cell"]}>
                      <TextField
                        size="small"
                        type="number"
                        value={quantity}
                        onChange={(e) => handleRowDataChange(row.modalId, "quantity", e.target.value)}
                        className={modalStyles["modal-input"]}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <div className={modalStyles["spinner-controls"]}>
                                <IconButton
                                  size="small"
                                  onClick={spinnerHandlers.onQuantityIncrement}
                                  className={modalStyles["spinner-button"]}
                                >
                                  {QUANTITY_SPINNER_UP}
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={spinnerHandlers.onQuantityDecrement}
                                  className={modalStyles["spinner-button"]}
                                >
                                  {QUANTITY_SPINNER_DOWN}
                                </IconButton>
                              </div>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </div>
                    <div className={modalStyles["modal-bidask-cell"]}>
                      <ModalBidAskField
                        orderType={orderType1}
                        onOrderTypeChange={(e) =>
                          handleRowDataChange(row.modalId, "orderType1", e.target.value)
                        }
                        value={bidAskPct1}
                        onChange={(e) => handleRowDataChange(row.modalId, "bidAskPct1", e.target.value)}
                        disabled={submitting}
                      />
                    </div>
                    <div className={modalStyles["modal-bidask-cell"]}>
                      <ModalBidAskField
                        orderType={orderType2}
                        onOrderTypeChange={(e) =>
                          handleRowDataChange(row.modalId, "orderType2", e.target.value)
                        }
                        value={bidAskPct2}
                        onChange={(e) => handleRowDataChange(row.modalId, "bidAskPct2", e.target.value)}
                        disabled={submitting}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      );
    });
  }, [
    createSpinnerHandlers,
    getRepriceTradingSymbol,
    getRowValue,
    groupedData,
    groupedDataKeys,
    handleRowDataChange,
    handleTradingSymbolChange,
    hasGroupedData,
    submitting,
  ]);

  const footer = (
    <>
      <Button
        variant="outlined"
        onClick={handleClose}
        disabled={submitting}
        className={modalStyles["modal-cancel-button"]}
      >
        Cancel
      </Button>
      <Button
        variant="contained"
        onClick={handleConfirm}
        disabled={submitting || !hasGroupedData}
        className={modalStyles["modal-sqoff-button"]}
      >
        Reprice{submitting ? "…" : ""}
      </Button>
    </>
  );

  return (
    <BookOrderDialogShell
      open={open}
      onClose={handleClose}
      paperClassName={modalStyles["reprice-modal-dialog-paper"]}
      layoutVariant="reprice"
      orderEntryGrid
      maxWidth={false}
      fullWidth={false}
      showTitleBar={false}
      header={header}
      footer={footer}
      closeDisabled={submitting}
    >
      {body}
    </BookOrderDialogShell>
  );
}

export default RepriceModal;
